export const $ = (sel, root = document) => root.querySelector(sel);
export const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

export const EMPTY_CELL_TEXT = "(none)";

const parked = new Map();

/**
 * Put an element in the page or take it out. Unlike hiding it, a removed
 * element is not in the DOM at all (not visible in the browser's dev tools),
 * for parts that only some people may use. Keep a reference to the element:
 * $() cannot find it while it is out. Its event listeners stay attached.
 */
export function setPresent(el, on) {
  const mark = parked.get(el);
  if (on) {
    if (mark) {
      mark.replaceWith(el);
      parked.delete(el);
    }
    el.classList.remove("d-none");
  } else if (!mark && el.isConnected) {
    const placeholder = document.createComment(el.id || "");
    el.replaceWith(placeholder);
    parked.set(el, placeholder);
  }
}

/** 1024-based human readable size, same as the desktop format_byte_size(). */
export function formatByteSize(n) {
  n = Number(n) || 0;
  if (n < 1024) return `${n} B`;
  const units = ["KB", "MB", "GB", "TB"];
  let value = n;
  for (let i = 0; i < units.length; i++) {
    value /= 1024;
    const rounded = Math.round(value * 10) / 10;
    if (rounded < 1024 || i === units.length - 1) return `${rounded.toFixed(1)} ${units[i]}`;
  }
  return `${n} B`;
}

export function escapeHtml(text) {
  return String(text ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[c]);
}

export function basename(path) {
  return String(path || "").split(/[\\/]/).pop();
}

export function suffixes(name) {
  const base = basename(name);
  const parts = base.split(".");
  return parts.length > 1 ? parts.slice(1).map((p) => `.${p.toLowerCase()}`) : [];
}

export function suffix(name) {
  const base = basename(name);
  const i = base.lastIndexOf(".");
  return i > 0 ? base.slice(i).toLowerCase() : "";
}

/** Same rule as utilities/drop_classifier.suffix_in_container(). */
export function suffixInContainer(fileName, container) {
  const s = suffix(fileName);
  if (!s || !container) return false;
  return suffixes(container).includes(s);
}

export function relTime(ms) {
  if (!ms) return "—";
  const diff = Date.now() - ms;
  const abs = Math.abs(diff);
  const units = [
    ["year", 365 * 864e5],
    ["month", 30 * 864e5],
    ["day", 864e5],
    ["hour", 36e5],
    ["minute", 6e4],
  ];
  for (const [unit, size] of units) {
    if (abs >= size) {
      const v = Math.round(diff / size);
      return new Intl.RelativeTimeFormat("en", { numeric: "auto" }).format(-v, unit);
    }
  }
  return diff >= 0 ? "just now" : "soon";
}

/** DD/MM/YYYY HH:MM in the device's time zone. */
export function fmtDate(ms) {
  if (!ms) return "—";
  const d = new Date(ms);
  const p = (n) => String(n).padStart(2, "0");
  return `${p(d.getDate())}/${p(d.getMonth() + 1)}/${d.getFullYear()} ${p(d.getHours())}:${p(d.getMinutes())}`;
}

export const isMac = /Mac|iPhone|iPad/.test(navigator.platform || navigator.userAgent);
export const isMobile = () => window.matchMedia("(max-width: 767.98px)").matches;

export function debounce(fn, ms) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn(...args), ms);
  };
}

export function deviceName(ua = "") {
  const browser = /Edg\//.test(ua) ? "Edge" : /Chrome\//.test(ua) ? "Chrome" : /Firefox\//.test(ua) ? "Firefox" : /Safari\//.test(ua) ? "Safari" : ua.split(" ")[0] || "Unknown";
  const os = /Windows/.test(ua) ? "Windows" : /Android/.test(ua) ? "Android" : /iPhone|iPad/.test(ua) ? "iOS" : /Mac OS/.test(ua) ? "macOS" : /Linux/.test(ua) ? "Linux" : "";
  return os ? `${browser} on ${os}` : browser;
}

/** Read the first bytes of a File and check for the UnityFS signature. */
export async function hasUnityFsHeader(file) {
  try {
    const buf = new Uint8Array(await file.slice(0, 8).arrayBuffer());
    return buf.length >= 8 && String.fromCharCode(...buf.slice(0, 7)) === "UnityFS";
  } catch {
    return false;
  }
}
